import React, { useState, useMemo } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Chip,
  Select,
  MenuItem,
  Input,
  SelectChangeEvent,
  SxProps,
  ListSubheader,
  TextField,
  IconButton,
} from "@mui/material";

import { formatColumnCaption } from "../../utils/mui_table_utils";

const CustomIcon = ({
  toggleOpen,
  open,
}: {
  toggleOpen: (e: React.MouseEvent) => void;
  open: boolean;
}) => (
  <Box
    onClick={toggleOpen}
    sx={{
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {open ? (
      <ArrowDropUpIcon fontSize="small" sx={{ color: "action.active" }} />
    ) : (
      <ArrowDropDownIcon fontSize="small" sx={{ color: "action.active" }} />
    )}
  </Box>
);

const toSafeString = (value: string | null | undefined) =>
  value == null ? "" : String(value);

const containsText = (
  text: string | null | undefined,
  searchText: string
) => {
  const normalizedText = toSafeString(text).toLowerCase();
  return normalizedText.indexOf(searchText.toLowerCase()) > -1;
};

export function SearchableDropdown({
  isLoading = false,
  placeholder,
  selectedValue,
  handleChange,
  options,
  sx,
  noFormatting = false,
  label,
}: {
  isLoading?: boolean;
  placeholder?: string;
  selectedValue: string;
  handleChange: (value: string) => void;
  options: Array<string | null | undefined>;
  sx?: SxProps;
  noFormatting?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const toggleOpen = (e: React.MouseEvent) => {
    setOpen((prev) => !prev);
  };

  const normalizedOptions = useMemo(
    () => options.map((option) => toSafeString(option)),
    [options]
  );

  const displayedOptions = useMemo(() => {
    const normalizedSearch = searchText.toLowerCase();
    return normalizedOptions
      .filter((option) => containsText(option, normalizedSearch))
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [normalizedOptions, searchText]);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    handleChange(event.target.value);
    setOpen(false);
    setSearchText("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        width: "fit-content",
        minWidth: "100px",
      }}
    >
      {label && selectedValue && (
        <Box sx={{ fontSize: 11, color: "text.secondary" }}>{label}</Box>
      )}
      <Select
        sx={{
          minWidth: "100px",
          width: "fit-content",
          ...sx,
        }}
        disabled={isLoading}
        variant="standard"
        size="small"
        value={selectedValue}
        onChange={handleSelectChange}
        displayEmpty
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearchText("");
        }}
        IconComponent={(props) => (
          <CustomIcon toggleOpen={toggleOpen} open={open} />
        )}
        input={
          <Input
            onClick={(e: React.MouseEvent<HTMLInputElement>) => {
              // Don't open if clicking on a delete button or chip
              const target = e.target as HTMLElement;
              if (
                target.closest("button") ||
                target.closest(".MuiChip-deleteIcon") ||
                target.closest(".MuiChip-root")
              ) {
                // Don't stop propagation - let Chip's onDelete handle it
                return;
              }
            }}
          />
        }
        renderValue={(selected) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              const target = e.target as HTMLElement;

              // Check if clicking on delete button
              const deleteButton =
                target.closest("button[aria-label*='Delete']") ||
                target.closest(".MuiChip-deleteIcon") ||
                (target.tagName === "BUTTON" &&
                  target.closest(".MuiChip-root"));

              if (deleteButton) {
                e.stopPropagation();
                e.preventDefault();
                handleChange("");
                return;
              }

              // Don't open menu if clicking on chips
              if (target.closest(".MuiChip-root")) {
                e.stopPropagation();
                return;
              }
            }}
          >
            {selected ? (
              <Chip
                label={noFormatting ? selected : formatColumnCaption(selected)}
                size="small"
                sx={{
                  backgroundColor: "action.selected",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  },
                }}
                onDelete={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleChange("");
                }}
                deleteIcon={
                  <IconButton
                    size="small"
                    onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleChange("");
                    }}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleChange("");
                    }}
                    sx={{
                      padding: 0,
                      margin: 0,
                      "&:hover": { backgroundColor: "transparent" },
                      "& svg": {
                        fontSize: "18px",
                      },
                    }}
                  >
                    <CancelIcon />
                  </IconButton>
                }
              />
            ) : (
              <Box sx={{ color: "text.secondary" }}>{label}</Box>
            )}
          </Box>
        )}
        MenuProps={{
          // Ensure click-away detection works properly
          disableAutoFocusItem: true,
        }}
      >
        <ListSubheader sx={{ lineHeight: "40px" }}>
          <TextField
            autoFocus
            placeholder="Type to search..."
            fullWidth
            variant="standard"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Escape") {
                // Prevents autoselecting item while typing (default Select behaviour)
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>
        {displayedOptions.map((option, index) => (
          <MenuItem key={index} value={option}>
            {noFormatting ? option : formatColumnCaption(option)}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}
